import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ShoppingCart, Plus, Minus, Trash2, X, Search, Package, Store as StoreIcon, CheckCircle2 } from 'lucide-react';
import { storeService, productImageUrl, StoreProduct } from '@/services/store';
import { formatPrice, toWhatsAppPhone } from '@/utils/format';

interface CartLine {
  id: number;
  name: string;
  price: number;
  unit: string;
  qty: number;
  maxStock: number | null; // null = sin límite
}

type PaymentMethod = 'transferencia' | 'efectivo';
type DeliveryMethod = 'delivery' | 'pickup';

// Abreviatura de unidad para los componentes de un combo (ej. 1u, 0,5kg)
const UNIT_ABBR: Record<string, string> = {
  unit: 'u', gram: 'g', kilogram: 'kg', liter: 'l', milliliter: 'ml',
};
function comboQtyLabel(quantity: number, unit?: string | null): string {
  const qty = Number(quantity);
  const qtyStr = Number.isInteger(qty) ? String(qty) : qty.toLocaleString('es-AR');
  const abbr = unit ? (UNIT_ABBR[unit] ?? '') : '';
  return `${qtyStr}${abbr}`;
}

export default function Storefront() {
  const { slug = '' } = useParams();
  const [search, setSearch] = useState('');
  const [categoryId, setCategoryId] = useState<number | null>(null);
  const [cart, setCart] = useState<CartLine[]>([]);
  const [cartOpen, setCartOpen] = useState(false);

  // Checkout
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerAddress, setCustomerAddress] = useState('');
  const [payment, setPayment] = useState<PaymentMethod>('transferencia');
  const [delivery, setDelivery] = useState<DeliveryMethod>('delivery');
  const [notes, setNotes] = useState('');

  const { data: store, isLoading: loadingStore, isError } = useQuery({
    queryKey: ['store', slug],
    queryFn: () => storeService.getStore(slug),
    retry: false,
  });
  const { data: categories = [] } = useQuery({
    queryKey: ['store-cats', slug],
    queryFn: () => storeService.getCategories(slug),
    enabled: !!store,
  });
  const { data: products = [], isLoading: loadingProducts } = useQuery({
    queryKey: ['store-products', slug, categoryId, search],
    queryFn: () => storeService.getProducts(slug, {
      category_id: categoryId || undefined,
      search: search.length >= 2 ? search : undefined,
    }),
    enabled: !!store,
  });

  const cartCount = cart.reduce((s, l) => s + l.qty, 0);
  const cartTotal = cart.reduce((s, l) => s + l.price * l.qty, 0);

  const addToCart = (p: StoreProduct) => {
    if (!p.available) return;
    const max = p.stock_available != null ? Number(p.stock_available) : null;
    setCart((prev) => {
      const ex = prev.find((l) => l.id === p.id);
      if (ex) {
        const next = ex.qty + 1;
        if (max != null && next > max) return prev; // no superar stock
        return prev.map((l) => (l.id === p.id ? { ...l, qty: next } : l));
      }
      return [...prev, { id: p.id, name: p.name, price: Number(p.price), unit: p.display_unit, qty: 1, maxStock: max }];
    });
    setCartOpen(true);
  };

  const setQty = (id: number, qty: number) => {
    setCart((prev) =>
      prev.flatMap((l) => {
        if (l.id !== id) return [l];
        if (qty <= 0) return [];
        if (l.maxStock != null && qty > l.maxStock) return [{ ...l, qty: l.maxStock }];
        return [{ ...l, qty }];
      })
    );
  };
  const removeLine = (id: number) => setCart((prev) => prev.filter((l) => l.id !== id));

  const [sending, setSending] = useState(false);
  const [sentOrderNumber, setSentOrderNumber] = useState<string | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);

  // La dirección solo es obligatoria si el envío es a domicilio (no para retiro en tienda)
  const addressOk = delivery === 'pickup' || !!customerAddress.trim();
  const canSend = cart.length > 0 && customerName.trim() && customerPhone.trim() && addressOk && store?.whatsapp;

  const handleSend = async () => {
    if (!store?.whatsapp || cart.length === 0) return;
    setSending(true);
    setSendError(null);
    try {
      // 1) Registrar el pedido en el sistema (queda como "pendiente")
      const order = await storeService.createOrder(slug, {
        customer_name: customerName.trim(),
        customer_phone: customerPhone.trim(),
        customer_address: delivery === 'delivery' ? (customerAddress.trim() || undefined) : undefined,
        payment_method: payment,
        delivery_method: delivery,
        notes: notes.trim() || undefined,
        items: cart.map((l) => ({ product_id: l.id, quantity: l.qty })),
      });

      // 2) Armar y abrir WhatsApp con el detalle
      const lines = cart
        .map((l) => `• ${l.qty} x ${l.name} — ${formatPrice(l.price)} c/u = ${formatPrice(l.price * l.qty)}`)
        .join('\n');
      let msg = `¡Hola ${store.name}! Pedido *#${order.order_number}*:\n\n${lines}\n\n*Total: ${formatPrice(cartTotal)}*\n\n`;
      msg += `Entrega: ${delivery === 'delivery' ? 'Envío a domicilio' : 'Retiro en tienda'}\n`;
      msg += `Pago: ${payment === 'transferencia' ? 'Transferencia (te envío el comprobante)' : 'Efectivo'}\n\n`;
      msg += `Mis datos:\nNombre: ${customerName}\nTel: ${customerPhone}`;
      if (delivery === 'delivery' && customerAddress.trim()) msg += `\nDirección: ${customerAddress}`;
      if (notes.trim()) msg += `\nNotas: ${notes}`;
      const phone = toWhatsAppPhone(store.whatsapp);
      window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, '_blank');

      // 3) Confirmación + limpiar
      setSentOrderNumber(order.order_number);
      setCart([]);
    } catch (e: any) {
      setSendError(e?.response?.data?.detail || 'No se pudo enviar el pedido. Intentá de nuevo.');
    } finally {
      setSending(false);
    }
  };

  if (loadingStore) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-cream-50 via-white to-cream-100">
        <div className="flex flex-col items-center gap-3 text-taupe-600">
          <div className="w-10 h-10 rounded-full border-2 border-taupe-200 border-t-taupe-600 animate-spin" />
          <span className="text-sm font-medium">Cargando tienda…</span>
        </div>
      </div>
    );
  }
  if (isError || !store) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-center p-6 bg-gradient-to-br from-cream-50 via-white to-cream-100">
        <div className="w-16 h-16 rounded-2xl bg-cream-100 border border-cream-200 flex items-center justify-center mb-4">
          <StoreIcon className="h-8 w-8 text-taupe-400" />
        </div>
        <h1 className="text-xl font-bold text-taupe-900">Tienda no encontrada</h1>
        <p className="text-taupe-500 mt-1">El link no es válido o la tienda no está disponible.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-cream-50 via-white to-cream-100">
      {/* Vista previa: esta tienda todavia no esta publicada y solo la ve la gente
          del local. Va arriba de todo y bien visible para que nadie crea que los
          clientes ya la estan viendo. */}
      {store.published === false && (
        <div className="sticky top-0 z-30 bg-amber-500 text-white text-center text-sm px-4 py-2 font-medium">
          Vista previa · Esta tienda todavía no está publicada: los clientes no la ven.
        </div>
      )}

      {/* Header */}
      <header className="sticky top-0 z-20 bg-white/80 backdrop-blur-md border-b border-cream-200">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            {store.logo_url ? (
              <img src={productImageUrl(store.logo_url)} alt="" className="h-11 w-11 rounded-xl object-cover ring-1 ring-cream-200" />
            ) : (
              <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-taupe-600 to-taupe-800 flex items-center justify-center shadow-sm">
                <StoreIcon className="h-5 w-5 text-cream-50" />
              </div>
            )}
            <div className="min-w-0">
              <h1 className="font-bold text-taupe-900 truncate leading-tight">{store.name}</h1>
              {store.description && <p className="text-xs text-taupe-500 truncate">{store.description}</p>}
            </div>
          </div>
          <button
            onClick={() => setCartOpen(true)}
            className="relative inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-taupe-700 text-cream-50 text-sm font-semibold hover:bg-taupe-800 transition-colors shadow-sm"
          >
            <ShoppingCart className="h-4 w-4" />
            <span className="hidden sm:inline">Carrito</span>
            {cartCount > 0 && (
              <span className="absolute -top-2 -right-2 bg-cream-500 text-taupe-900 text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center ring-2 ring-white">
                {cartCount}
              </span>
            )}
          </button>
        </div>
      </header>

      {/* Hero band */}
      <section className="relative overflow-hidden border-b border-cream-200">
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-0 right-0 w-72 h-72 bg-taupe-200/40 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-10 left-0 w-64 h-64 bg-cream-300/50 rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-6xl mx-auto px-4 py-8 sm:py-10">
          <p className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-taupe-100 text-taupe-700 text-xs font-semibold mb-3">
            <StoreIcon className="h-3.5 w-3.5" /> Tienda online
          </p>
          <h2 className="text-2xl sm:text-3xl font-bold text-taupe-900">{store.name}</h2>
          {store.description ? (
            <p className="mt-2 text-taupe-600 max-w-2xl">{store.description}</p>
          ) : (
            <p className="mt-2 text-taupe-600 max-w-2xl">Elegí tus productos y hacé tu pedido en segundos.</p>
          )}
        </div>
      </section>

      <main className="max-w-6xl mx-auto px-4 py-6 pb-28 sm:pb-8">
        {/* Buscador + categorías */}
        <div className="mb-6 space-y-3">
          <div className="relative">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-taupe-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar productos…"
              className="w-full pl-11 pr-4 py-3 bg-white border border-cream-200 rounded-2xl text-taupe-900 placeholder:text-taupe-400 focus:outline-none focus:ring-2 focus:ring-taupe-400 focus:border-taupe-400 shadow-sm transition"
            />
          </div>
          {categories.length > 0 && (
            <div className="flex gap-2 overflow-x-auto pb-1">
              <button
                onClick={() => setCategoryId(null)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${categoryId === null ? 'bg-taupe-700 text-cream-50 shadow-sm' : 'bg-white border border-cream-200 text-taupe-600 hover:border-taupe-300'}`}
              >
                Todos
              </button>
              {categories.map((c) => (
                <button
                  key={c.id}
                  onClick={() => setCategoryId(c.id)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${categoryId === c.id ? 'bg-taupe-700 text-cream-50 shadow-sm' : 'bg-white border border-cream-200 text-taupe-600 hover:border-taupe-300'}`}
                >
                  {c.name}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Grilla de productos */}
        {loadingProducts ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="bg-white rounded-2xl border border-cream-200 overflow-hidden animate-pulse">
                <div className="aspect-square bg-cream-100" />
                <div className="p-4 space-y-2">
                  <div className="h-4 bg-cream-100 rounded w-3/4" />
                  <div className="h-4 bg-cream-100 rounded w-1/3" />
                  <div className="h-9 bg-cream-100 rounded-xl mt-2" />
                </div>
              </div>
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 rounded-2xl bg-cream-100 border border-cream-200 flex items-center justify-center mx-auto mb-3">
              <Package className="h-8 w-8 text-taupe-300" />
            </div>
            <p className="text-taupe-500 font-medium">No hay productos para mostrar.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {products.map((p) => {
              const img = productImageUrl(p.image_url);
              return (
                <div
                  key={p.id}
                  className="group bg-white rounded-2xl border border-cream-200 overflow-hidden flex flex-col hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200"
                >
                  <div className="relative aspect-square bg-cream-50 flex items-center justify-center overflow-hidden">
                    {img ? (
                      <img src={img} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                    ) : (
                      <Package className="h-12 w-12 text-cream-300" />
                    )}
                    {p.is_combo && (
                      <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-cream-500 text-taupe-900 text-[11px] font-bold shadow-sm">
                        Combo
                      </span>
                    )}
                    {!p.available && (
                      <div className="absolute inset-0 bg-white/60 backdrop-blur-[1px] flex items-center justify-center">
                        <span className="px-3 py-1 rounded-full bg-taupe-900/80 text-cream-50 text-xs font-semibold">Sin stock</span>
                      </div>
                    )}
                  </div>
                  <div className="p-4 flex flex-col flex-1">
                    <p className="text-sm font-semibold text-taupe-900 line-clamp-2 leading-snug">{p.name}</p>
                    {p.is_combo && p.components && p.components.length > 0 && (
                      <div className="mt-1.5 rounded-lg bg-cream-50 border border-cream-200 px-2 py-1.5">
                        <p className="text-[10px] font-bold uppercase tracking-wide text-taupe-400 mb-0.5">Incluye</p>
                        <ul className="text-xs text-taupe-600 space-y-0.5">
                          {p.components.map((c, i) => (
                            <li key={i} className="flex gap-1.5">
                              <span className="text-taupe-400">•</span>
                              <span className="line-clamp-1">{comboQtyLabel(c.quantity, c.unit)} {c.name}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    <p className="text-lg text-taupe-800 font-bold mt-1.5">{formatPrice(p.price)}</p>
                    <div className="mt-auto pt-3">
                      {p.available ? (
                        <button
                          onClick={() => addToCart(p)}
                          className="w-full inline-flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-taupe-700 text-cream-50 text-sm font-semibold hover:bg-taupe-800 active:scale-[0.98] transition"
                        >
                          <Plus className="h-4 w-4" /> Agregar
                        </button>
                      ) : (
                        <button disabled className="w-full py-2.5 rounded-xl bg-cream-100 text-taupe-400 text-sm font-semibold cursor-not-allowed">
                          No disponible
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* Barra fija inferior (solo mobile): atajo al carrito con total */}
      {cartCount > 0 && !cartOpen && (
        <div className="sm:hidden fixed bottom-0 inset-x-0 z-20 px-3 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] bg-gradient-to-t from-cream-100 via-cream-50/95 to-transparent">
          <button
            onClick={() => setCartOpen(true)}
            className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-2xl bg-taupe-700 text-cream-50 shadow-lg active:scale-[0.99] transition"
          >
            <span className="flex items-center gap-2 font-semibold">
              <span className="relative">
                <ShoppingCart className="h-5 w-5" />
                <span className="absolute -top-2 -right-2 bg-cream-500 text-taupe-900 text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center">{cartCount}</span>
              </span>
              Ver carrito
            </span>
            <span className="font-bold">{formatPrice(cartTotal)}</span>
          </button>
        </div>
      )}

      {/* Carrito (drawer) */}
      {cartOpen && (
        <div className="fixed inset-0 z-30 flex justify-end">
          <div className="absolute inset-0 bg-taupe-900/40 backdrop-blur-[2px]" onClick={() => setCartOpen(false)} />
          <div className="relative w-full max-w-md bg-cream-50 h-full flex flex-col shadow-2xl">
            <div className="flex items-center justify-between p-4 border-b border-cream-200 bg-white">
              <h2 className="font-bold text-taupe-900 flex items-center gap-2">
                <ShoppingCart className="h-5 w-5 text-taupe-600" /> Tu pedido
              </h2>
              <button onClick={() => setCartOpen(false)} className="p-1.5 rounded-lg text-taupe-400 hover:text-taupe-700 hover:bg-cream-100 transition">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {sentOrderNumber ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 rounded-full bg-taupe-100 mx-auto flex items-center justify-center mb-3">
                    <CheckCircle2 className="h-8 w-8 text-taupe-600" />
                  </div>
                  <h3 className="text-lg font-bold text-taupe-900">¡Pedido enviado!</h3>
                  <p className="text-sm text-taupe-600 mt-1">Tu pedido <strong>#{sentOrderNumber}</strong> fue registrado.</p>
                  <p className="text-sm text-taupe-600">Te abrimos WhatsApp para confirmarlo con la tienda.</p>
                  <button
                    onClick={() => { setSentOrderNumber(null); setCartOpen(false); }}
                    className="mt-5 px-5 py-2.5 rounded-xl bg-taupe-700 text-cream-50 text-sm font-semibold hover:bg-taupe-800 transition"
                  >
                    Seguir comprando
                  </button>
                </div>
              ) : cart.length === 0 ? (
                <div className="text-center py-16">
                  <div className="w-14 h-14 rounded-2xl bg-cream-100 border border-cream-200 flex items-center justify-center mx-auto mb-3">
                    <ShoppingCart className="h-7 w-7 text-taupe-300" />
                  </div>
                  <p className="text-taupe-500">Tu carrito está vacío.</p>
                </div>
              ) : (
                cart.map((l) => (
                  <div key={l.id} className="flex items-center gap-3 bg-white rounded-xl border border-cream-200 p-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-taupe-900 truncate">{l.name}</p>
                      <p className="text-xs text-taupe-500">{formatPrice(l.price)} c/u</p>
                    </div>
                    <div className="flex items-center gap-1">
                      <button onClick={() => setQty(l.id, l.qty - 1)} className="w-7 h-7 rounded-lg bg-cream-100 hover:bg-cream-200 text-taupe-700 flex items-center justify-center transition"><Minus className="h-3.5 w-3.5" /></button>
                      <span className="w-7 text-center text-sm font-semibold text-taupe-900">{l.qty}</span>
                      <button onClick={() => setQty(l.id, l.qty + 1)} disabled={l.maxStock != null && l.qty >= l.maxStock} className="w-7 h-7 rounded-lg bg-cream-100 hover:bg-cream-200 text-taupe-700 flex items-center justify-center disabled:opacity-40 transition"><Plus className="h-3.5 w-3.5" /></button>
                      <button onClick={() => removeLine(l.id)} className="w-7 h-7 rounded-lg text-rose-500 hover:bg-rose-50 flex items-center justify-center transition"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                    <p className="w-20 text-right text-sm font-bold text-taupe-900">{formatPrice(l.price * l.qty)}</p>
                  </div>
                ))
              )}

              {cart.length > 0 && !sentOrderNumber && (
                <div className="pt-2 space-y-3">
                  <div className="flex justify-between font-bold text-taupe-900 text-lg px-1">
                    <span>Total</span><span>{formatPrice(cartTotal)}</span>
                  </div>

                  {/* Datos del cliente */}
                  <div className="space-y-2 pt-1">
                    <input value={customerName} onChange={(e) => setCustomerName(e.target.value)} placeholder="Tu nombre *" className="w-full px-3.5 py-2.5 bg-white border border-cream-200 rounded-xl text-sm text-taupe-900 placeholder:text-taupe-400 focus:outline-none focus:ring-2 focus:ring-taupe-400" />
                    <input value={customerPhone} onChange={(e) => setCustomerPhone(e.target.value)} placeholder="Tu teléfono *" className="w-full px-3.5 py-2.5 bg-white border border-cream-200 rounded-xl text-sm text-taupe-900 placeholder:text-taupe-400 focus:outline-none focus:ring-2 focus:ring-taupe-400" />

                    {/* Tipo de entrega */}
                    <p className="text-xs font-medium text-taupe-500 pt-1">¿Cómo lo recibís?</p>
                    <div className="grid grid-cols-2 gap-2">
                      {([['delivery', 'Envío a domicilio'], ['pickup', 'Retiro en tienda']] as [DeliveryMethod, string][]).map(([m, label]) => (
                        <button key={m} type="button" onClick={() => setDelivery(m)}
                          className={`py-2.5 rounded-xl text-sm font-semibold border transition ${delivery === m ? 'bg-taupe-100 border-taupe-500 text-taupe-800' : 'bg-white border-cream-200 text-taupe-500 hover:border-taupe-300'}`}>
                          {label}
                        </button>
                      ))}
                    </div>

                    {delivery === 'delivery' && (
                      <input value={customerAddress} onChange={(e) => setCustomerAddress(e.target.value)} placeholder="Tu dirección *" className="w-full px-3.5 py-2.5 bg-white border border-cream-200 rounded-xl text-sm text-taupe-900 placeholder:text-taupe-400 focus:outline-none focus:ring-2 focus:ring-taupe-400" />
                    )}

                    {/* Método de pago */}
                    <p className="text-xs font-medium text-taupe-500 pt-1">¿Cómo pagás?</p>
                    <div className="grid grid-cols-2 gap-2">
                      {(['transferencia', 'efectivo'] as PaymentMethod[]).map((m) => (
                        <button key={m} type="button" onClick={() => setPayment(m)}
                          className={`py-2.5 rounded-xl text-sm font-semibold border capitalize transition ${payment === m ? 'bg-taupe-100 border-taupe-500 text-taupe-800' : 'bg-white border-cream-200 text-taupe-500 hover:border-taupe-300'}`}>
                          {m}
                        </button>
                      ))}
                    </div>
                    {payment === 'transferencia' && store.transfer_info && (
                      <div className="bg-cream-100 border border-cream-200 rounded-xl p-3 text-xs text-taupe-700 whitespace-pre-line">
                        <strong className="block mb-1 text-taupe-900">Datos para transferir:</strong>
                        {store.transfer_info}
                      </div>
                    )}
                    <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Notas / aclaraciones (opcional)" className="w-full px-3.5 py-2.5 bg-white border border-cream-200 rounded-xl text-sm text-taupe-900 placeholder:text-taupe-400 focus:outline-none focus:ring-2 focus:ring-taupe-400 resize-none" />
                  </div>
                </div>
              )}
            </div>

            {cart.length > 0 && !sentOrderNumber && (
              <div className="px-4 pt-4 pb-[calc(1rem+env(safe-area-inset-bottom))] border-t border-cream-200 bg-white">
                {sendError && <p className="text-xs text-rose-500 text-center mb-2">{sendError}</p>}
                <button
                  onClick={handleSend}
                  disabled={!canSend || sending}
                  className="w-full py-3.5 rounded-xl bg-[#25D366] text-white font-bold hover:brightness-95 disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center justify-center gap-2"
                >
                  {sending ? 'Enviando…' : 'Enviar pedido por WhatsApp'}
                </button>
                {!store.whatsapp && (
                  <p className="text-xs text-rose-500 text-center mt-2">La tienda no configuró WhatsApp.</p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
